import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';

export interface DatacenterConfig {
  id: number;
  code: string;
  name: string;
  rows_count: number;
  max_racks_per_row: number;
  rack_step_m: number;
  aisle_width_m: number;
  rack_depth_m: number;
  rack_height_m: number;
  tray_above_rack_m: number;
  cable_slack_m: number;
  rack_naming_template: string;
  prefix: string;
  routing_strategy: string;
  cable_trays_json: any;
  aisle_zones_json?: any;
  ai_enabled: boolean;
}

export interface Draft {
  id: number;
  dc_code: string;
  revision: number;
  base_revision: number;
  operations_json: any[];
  name: string;
  rows_count?: number;
  max_racks_per_row?: number;
  racks_json?: any;
  cable_trays_json?: any;
  published: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DraftRebaseChange {
  kind: string;
  key?: string | null;
  before: any;
  after: any;
  draft?: unknown;
  allowed_resolutions?: string[];
  discarded_fields?: string[];
  retained_fields?: string[];
  name_only?: boolean;
  geometry_choice?: boolean;
  topology_conflict?: boolean;
}

export interface DraftRebasePreview {
  stale: boolean;
  draft_revision: number;
  base_revision: number;
  production_revision: number;
  production_token: string;
  automatic: DraftRebaseChange[];
  conflicts: DraftRebaseChange[];
  scenes?: { base: any; published: any; draft: any | null };
}

export interface FloorPlanRevision {
  revision: number;
  snapshot_json: any;
  author: string;
  source_draft_id?: number | null;
  restore_of_revision?: number | null;
  reason?: string | null;
  created_at?: string;
}

export interface FloorPlanRestorePreview {
  revision: number;
  current_revision: number;
  inverse_diff: any;
}

export interface FloorPlanValidationIssue {
  severity: 'Error' | 'Warning' | 'Info';
  code: string;
  message: string;
}

export interface FloorPlanValidationReport {
  revision: number;
  can_publish: boolean;
  issues: FloorPlanValidationIssue[];
}

export interface FloorAccess {
  view: boolean;
  edit: boolean;
  publish: boolean;
}

export function useFloorAccess(code: string) {
  return useQuery<FloorAccess>({
    queryKey: ['floor-access', code],
    queryFn: () => api.get(`/api/datacenters/${code}/floor-access`).then(r => r.data),
    enabled: !!code,
  });
}

export interface Template {
  id: number;
  code: string;
  name: string;
  description?: string;
  is_public: boolean;
}

export function useDatacenter(code: string) {
  return useQuery({
    queryKey: ['datacenter', code],
    queryFn: () => api.get(`/api/datacenters/${code}`).then(r => r.data as DatacenterConfig),
    enabled: !!code,
  });
}

export function useDrafts(code: string) {
  return useQuery({
    queryKey: ['drafts', code],
    queryFn: () => api.get(`/api/datacenters/${code}/drafts`).then(r => r.data as Draft[]),
    enabled: !!code,
  });
}

export function useTemplates(code: string) {
  return useQuery({
    queryKey: ['templates', code],
    queryFn: () => api.get(`/api/datacenters/${code}/templates`).then(r => r.data as Template[]),
    enabled: !!code,
  });
}

export function useCreateDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: (draft: Partial<Draft>) =>
      api.post(`/api/datacenters/${code}/drafts`, draft).then(r => r.data as Draft),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts', code] }),
  });
}

export function useUpdateDraft(code: string) {
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ draftId, racks_json, cable_trays_json, expected_revision }: {
      draftId: number;
      racks_json: any;
      cable_trays_json: any;
      expected_revision?: number;
    }) => api.put(`/api/datacenters/${code}/drafts/${draftId}`, {
      racks_json,
      cable_trays_json,
      expected_revision,
    }).then(r => r.data as Draft),
  });
}

export function usePublishDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, expectedRevision, warningOverrides, approvalId, reason, ticket, changeSetId }: { draftId: number; expectedRevision: number; warningOverrides?: Array<{ code: string; reason: string }>; approvalId?: number; reason?: string; ticket?: string; changeSetId?: string }) =>
      api.post(`/api/datacenters/${code}/drafts/${draftId}/publish`, {
        expected_revision: expectedRevision,
        warning_overrides: warningOverrides ?? [],
        approval_id: approvalId,
        reason,
        ticket,
        change_set_id: changeSetId,
      }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts', code] });
      qc.invalidateQueries({ queryKey: ['datacenter', code] });
      qc.invalidateQueries({ queryKey: ['floor-revisions', code] });
    },
  });
}

export function useDraftValidation(code: string, draftId: number | null) {
  return useQuery<FloorPlanValidationReport>({
    queryKey: ['draft-validation', code, draftId],
    queryFn: () => api.get(`/api/datacenters/${code}/drafts/${draftId}/validation`).then(r => r.data),
    enabled: !!code && draftId != null,
  });
}

export function useDraftRebasePreview(code: string, draftId: number | null) {
  return useQuery<DraftRebasePreview>({
    queryKey: ['draft-rebase-preview', code, draftId],
    queryFn: () => api.get(`/api/datacenters/${code}/drafts/${draftId}/rebase-preview`).then(r => r.data),
    enabled: !!code && draftId != null,
  });
}

export function useRebaseDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, expectedRevision, productionToken, resolutions }: {
      draftId: number;
      expectedRevision: number;
      productionToken: string;
      resolutions?: Record<string, string>;
    }) => api.post(`/api/datacenters/${code}/drafts/${draftId}/rebase`, {
      expected_revision: expectedRevision,
      production_token: productionToken,
      resolutions: resolutions ?? {},
    }).then(r => r.data as Draft),
    onSuccess: (_draft, variables) => {
      qc.invalidateQueries({ queryKey: ['drafts', code] });
      qc.invalidateQueries({ queryKey: ['draft-rebase-preview', code, variables.draftId] });
    },
  });
}

export function useFloorPlanRevisions(code: string) {
  return useQuery<FloorPlanRevision[]>({
    queryKey: ['floor-revisions', code],
    queryFn: () => api.get(`/api/datacenters/${code}/floor-revisions`).then(r => r.data),
    enabled: !!code,
  });
}

export function useFloorPlanRestorePreview(code: string, revision: number | null) {
  return useQuery<FloorPlanRestorePreview>({
    queryKey: ['floor-restore-preview', code, revision],
    queryFn: () => api.get(`/api/datacenters/${code}/floor-revisions/${revision}/restore-preview`).then(r => r.data),
    enabled: !!code && revision != null,
  });
}

export function useRestoreFloorPlan(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ revision, expectedProductionRevision, reason }: {
      revision: number;
      expectedProductionRevision: number;
      reason: string;
    }) => api.post(`/api/datacenters/${code}/floor-revisions/${revision}/restore`, {
      expected_production_revision: expectedProductionRevision,
      reason,
    }).then(r => r.data as Draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts', code] });
      qc.invalidateQueries({ queryKey: ['floor-revisions', code] });
    },
  });
}

export function useDeleteDraft(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) =>
      api.delete(`/api/datacenters/${code}/drafts/${draftId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts', code] }),
  });
}

export function useApplyTemplate(code: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateCode: string) =>
      api.post(`/api/datacenters/${code}/templates/${templateCode}/apply`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts', code] });
      qc.invalidateQueries({ queryKey: ['datacenter', code] });
    },
  });
}

export function useExportDatacenter(code: string) {
  return useMutation({
    mutationFn: () =>
      api.post(`/api/datacenters/${code}/export`).then(r => r.data),
  });
}
